import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { VERSION_KEY } from '../../utils/version';
import styles from '../styles/QuickInfoContainer.module.css';
import { getThemeForPage } from '../styles/themes';
import { clearAllAuthData } from '../utils/auth';
import { BellIcon, RefreshIcon, UpdateIcon } from './icons/ActionIcons';
import useNotificationDeviceStatus from '../hooks/useNotificationDeviceStatus';
import {
  NOTIFICATION_DEVICE_STATUS,
  SCHEDULE_NOTIFICATION_STATUS
} from '../services/notificationService';

const QUICK_INFO_TEXT = {
  ko: {
    hello: '안녕',
    today: '오늘',
    year: '년',
    month: '월',
    day: '일',
    cacheClearSuccess: '오래된 캐시를 삭제했습니다. 이제부터 최신버전으로 보일것입니다.',
    cacheClearError: '캐시 지우기 중 오류가 발생했습니다.',
    updateReflection: '업데이트 반영',
    versionUpdateNeeded: '새 버전 업데이트 필요!',
    refresh: '새로고침',
    notificationSettings: '알림 설정',
    notificationActive: '알림 설정 · 이 기기 알림 켜짐',
    notificationInactive: '알림 설정 · 이 기기 알림 꺼짐',
    notificationUnavailable: '알림 설정 · 이 기기에서 알림 사용 불가',
    scheduleNotificationInactive: '알림 설정 · 일정 알림 꺼짐',
    scheduleNotificationInactiveConnected: '알림 설정 · 일정 알림 꺼짐 · 공지 알림 연결됨'
  },
  en: {
    hello: 'Hello',
    today: 'Today',
    year: '',
    month: '',
    day: '',
    cacheClearSuccess: 'Old cache has been deleted. You will now see the latest version.',
    cacheClearError: 'An error occurred while clearing cache.',
    updateReflection: 'Update Reflection',
    versionUpdateNeeded: 'New version update needed!',
    refresh: 'Refresh',
    notificationSettings: 'Notification settings',
    notificationActive: 'Notification settings · Notifications on for this device',
    notificationInactive: 'Notification settings · Notifications off for this device',
    notificationUnavailable: 'Notification settings · Notifications unavailable on this device',
    scheduleNotificationInactive: 'Notification settings · Schedule notifications off',
    scheduleNotificationInactiveConnected: 'Notification settings · Schedule notifications off · Notice notifications connected'
  },
  fr: {
    hello: 'Bonjour',
    today: "Aujourd'hui",
    year: '',
    month: '',
    day: '',
    cacheClearSuccess: 'L\'ancien cache a été supprimé. Vous verrez maintenant la dernière version.',
    cacheClearError: 'Une erreur s\'est produite lors de l\'effacement du cache.',
    updateReflection: 'Réflexion de mise à jour',
    versionUpdateNeeded: 'Mise à jour de nouvelle version nécessaire!',
    refresh: 'Actualiser',
    notificationSettings: 'Paramètres de notification',
    notificationActive: 'Paramètres de notification · Notifications activées sur cet appareil',
    notificationInactive: 'Paramètres de notification · Notifications désactivées sur cet appareil',
    notificationUnavailable: 'Paramètres de notification · Notifications indisponibles sur cet appareil',
    scheduleNotificationInactive: 'Paramètres de notification · Notifications de planning désactivées',
    scheduleNotificationInactiveConnected: 'Paramètres de notification · Planning désactivé · Notifications d’annonce connectées'
  }
};

export default function QuickInfoContainer() {
  const { userInfo } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [texts, setTexts] = useState([]);
  const [isVersionUpdateNeeded, setIsVersionUpdateNeeded] = useState(false);

  const lang = userInfo?.preferred_language || 'ko';
  const {
    status: notificationDeviceStatus,
    scheduleStatus: scheduleNotificationStatus
  } = useNotificationDeviceStatus(
    userInfo?.user_id,
    userInfo?.level
  );
  const isScheduleNotificationMuted = scheduleNotificationStatus.state
    === SCHEDULE_NOTIFICATION_STATUS.DISABLED;
  const isNotificationMuted = [
    NOTIFICATION_DEVICE_STATUS.INACTIVE,
    NOTIFICATION_DEVICE_STATUS.UNAVAILABLE
  ].includes(notificationDeviceStatus.state) || isScheduleNotificationMuted;
  const notificationVisualState = notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.UNAVAILABLE
    ? NOTIFICATION_DEVICE_STATUS.UNAVAILABLE
    : notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.INACTIVE
      ? NOTIFICATION_DEVICE_STATUS.INACTIVE
      : isScheduleNotificationMuted
        ? 'schedule-disabled'
        : notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.ACTIVE
          && scheduleNotificationStatus.state === SCHEDULE_NOTIFICATION_STATUS.ENABLED
          ? NOTIFICATION_DEVICE_STATUS.ACTIVE
          : NOTIFICATION_DEVICE_STATUS.CHECKING;
  const notificationButtonText = notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.UNAVAILABLE
    ? QUICK_INFO_TEXT[lang].notificationUnavailable
    : notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.INACTIVE
      ? QUICK_INFO_TEXT[lang].notificationInactive
      : isScheduleNotificationMuted
        ? notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.ACTIVE
          ? QUICK_INFO_TEXT[lang].scheduleNotificationInactiveConnected
          : QUICK_INFO_TEXT[lang].scheduleNotificationInactive
        : notificationDeviceStatus.state === NOTIFICATION_DEVICE_STATUS.ACTIVE
          && scheduleNotificationStatus.state === SCHEDULE_NOTIFICATION_STATUS.ENABLED
          ? QUICK_INFO_TEXT[lang].notificationActive
          : QUICK_INFO_TEXT[lang].notificationSettings;

  // 테마 설정
  const currentTheme = getThemeForPage(location.pathname);

  // 테마에 따른 퀵인포박스 배경색 설정 (메모이제이션)
  const quickInfoTheme = useMemo(() => {
    const isDosThemePage = location.pathname === '/client' || location.pathname === '/me' || location.pathname === '/settings';
    const userSelectedTheme = localStorage.getItem('user_theme') || 'modern';
    const isDosTheme = userSelectedTheme === 'dos';
    const isCalendarPage = location.pathname === '/calendar' || location.pathname === '/public-calendar';

    if (isCalendarPage) {
      return {
        ...currentTheme,
        backgroundColor: '#fffdf8', // 기본모드: Warm Ivory 배경
        borderColor: '#666666', // 기본모드: 짙은 회색 테두리
        inactiveColor: '#333333', // 기본모드: 검은색 글자
        activeColor: '#c00d0d', // 기본모드: 빨간색 활성 색상
        borderRadius: '20px' // 기본모드: 둥근 모서리
      };
    }

    if (isDosThemePage && isDosTheme) {
      return {
        ...currentTheme,
        backgroundColor: '#151515', // 도스모드: 검은색 배경
        borderColor: 'transparent', // 테두리 제거
        inactiveColor: '#f0f0f0', // 도스모드: 오프화이트 글자
        activeColor: '#00ff00', // 도스모드: 초록색 활성 색상
        borderRadius: '0' // 도스모드: 직각 모서리
      };
    }

    return {
      ...currentTheme,
      backgroundColor: '#fffdf8', // 일반모드: Warm Ivory 배경
      borderColor: '#666666', // 일반모드: 짙은 회색 테두리
      inactiveColor: '#333333', // 일반모드: 검은색 글자
      activeColor: '#c00d0d', // 일반모드: 빨간색 활성 색상
      borderRadius: '20px' // 일반모드: 둥근 모서리
    };
  }, [currentTheme, location.pathname]);

  // useCallback으로 함수 메모이제이션
  const getWelcomeText = useCallback(() => {
    return `${QUICK_INFO_TEXT[lang].hello} ! ${userInfo?.user_name || 'User'}`;
  }, [lang, userInfo?.user_name]);

  const getTodayText = useCallback(() => {
    const now = new Date();

    if (lang === 'ko') {
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const date = now.getDate();
      const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
      const dayName = dayNames[now.getDay()];

      return `${QUICK_INFO_TEXT[lang].today} : ${year}${QUICK_INFO_TEXT[lang].year} ${month}${QUICK_INFO_TEXT[lang].month} ${date}${QUICK_INFO_TEXT[lang].day} ${dayName}요일`;
    } else if (lang === 'en') {
      return `${QUICK_INFO_TEXT[lang].today}: ${now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })}`;
    } else if (lang === 'fr') {
      return `${QUICK_INFO_TEXT[lang].today}: ${now.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })}`;
    }
  }, [lang]);

  useEffect(() => {
    const newTexts = [
      getWelcomeText(),
      getTodayText()
    ];
    setTexts(newTexts);
  }, [getWelcomeText, getTodayText]);

  useEffect(() => {
    if (texts.length === 0) return;

    // 즉시 첫 번째 텍스트 표시
    setIsVisible(true);
    setIsHiding(false);

    const interval = setInterval(() => {
      setIsHiding(true);
      setTimeout(() => {
        setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
        setIsVisible(false);
        setIsHiding(false);

        // 다음 텍스트 표시 (아래에서 위로)
        setTimeout(() => {
          setIsVisible(true);
          setIsHiding(false);
        }, 20);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, [texts.length]);

  // 버전 업데이트 필요 상태 감지 (HTML 인라인 스크립트에서 설정한 값 확인)
  useEffect(() => {
    const checkVersionUpdateNeeded = () => {
      // HTML 인라인 스크립트에서 이미 설정했는지 확인
      if (window.latestServerVersion) {
        setIsVersionUpdateNeeded(true);
        console.log('Version update detected:', window.latestServerVersion);
      } else {
        setIsVersionUpdateNeeded(false);
      }
    };

    // 초기 체크 (HTML 스크립트 완료 대기)
    const timeoutId = setTimeout(checkVersionUpdateNeeded, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // 캐시 지우기 핸들러 - iOS PWA 완전 호환
  const handleClearCache = useCallback(async () => {
    try {
      const newVersion = window.latestServerVersion;

      // 1. Service Worker에 캐시 삭제 요청 (SW가 있는 경우)
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage('clearAllCaches');
        console.log('Sent clearAllCaches message to SW');
        // SW가 캐시 삭제할 시간 대기
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 2. 직접 Cache Storage 삭제 (백업)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Caches deleted:', cacheNames);
      }

      // 3. Service Worker 등록과 Push 구독은 유지한다.
      // 캐시 삭제만으로 새 정적 리소스를 가져오며, 등록 해제는 알림 구독을 끊을 수 있다.

      // 4. 인증 관련 저장소 정리
      clearAllAuthData();
      localStorage.removeItem('csrfToken');

      // 5. 로컬 스토리지 지우기 후 새 버전만 복원
      localStorage.clear();
      if (newVersion) {
        localStorage.setItem(VERSION_KEY, newVersion);
        console.log('Version updated to:', newVersion);
      }

      alert(QUICK_INFO_TEXT[lang].cacheClearSuccess);

      // 6. 강제 하드 리로드 (iOS Safari/PWA용 cache-busting)
      const url = new URL(window.location.href);
      url.searchParams.set('_cb', Date.now().toString());
      window.location.href = url.toString();
    } catch (error) {
      console.error('캐시 지우기 오류:', error);
      alert(QUICK_INFO_TEXT[lang].cacheClearError);
    }
  }, [lang]);

  return (
    <div
      className={styles.quickInfoContainer}
      style={{
        backgroundColor: quickInfoTheme.backgroundColor,
        borderColor: quickInfoTheme.borderColor,
        color: quickInfoTheme.inactiveColor,
        '--active-color': quickInfoTheme.activeColor,
        '--inactive-color': quickInfoTheme.inactiveColor,
        '--background-color': quickInfoTheme.backgroundColor,
        '--border-color': quickInfoTheme.borderColor,
        '--border-radius': quickInfoTheme.borderRadius
      }}
    >
      {/* 캐시 지우기 버튼 */}
      <button
        type="button"
        className={`${styles.quickActionButton} ${isVersionUpdateNeeded ? styles.versionUpdateNeeded : ''}`}
        onClick={handleClearCache}
        title={isVersionUpdateNeeded ? QUICK_INFO_TEXT[lang].versionUpdateNeeded : QUICK_INFO_TEXT[lang].updateReflection}
        aria-label={isVersionUpdateNeeded ? QUICK_INFO_TEXT[lang].versionUpdateNeeded : QUICK_INFO_TEXT[lang].updateReflection}
      >
        <UpdateIcon />
        {isVersionUpdateNeeded && (
          <span className={styles.updateText}>UPDATE</span>
        )}
      </button>

      {/* 알림 설정 버튼: row-reverse 화면에서 새로고침과 업데이트 사이 */}
      <button
        type="button"
        className={`${styles.quickActionButton} ${location.pathname === '/notification-settings' ? styles.active : ''}`}
        onClick={() => navigate('/notification-settings')}
        title={notificationButtonText}
        aria-label={notificationButtonText}
        aria-current={location.pathname === '/notification-settings' ? 'page' : undefined}
        data-notification-state={notificationVisualState}
      >
        <BellIcon muted={isNotificationMuted} />
      </button>

      {/* 새로고침 버튼 */}
      <button
        type="button"
        className={styles.quickActionButton}
        onClick={handleRefresh}
        title={QUICK_INFO_TEXT[lang].refresh}
        aria-label={QUICK_INFO_TEXT[lang].refresh}
      >
        <RefreshIcon />
      </button>
      <div
        key={currentTextIndex}
        className={`${styles.textContainer} ${isVisible ? styles.visible : ''} ${isHiding ? styles.hiding : ''}`}
      >
        {texts[currentTextIndex]}
      </div>
    </div>
  );
}
